/**
 * AT-LP120-style turntable platter with strobe dots, felt mat, and start/stop button.
 * Uses useAnimationFrame for smooth acceleration/deceleration of the platter rotation.
 * Children (VinylRecord) are rendered inside the platter and counter-rotated when seated.
 * Scratch ripple effects are rendered in a non-rotating overlay above the vinyl.
 *
 * Performance: rotation & speed are tracked via refs with direct DOM style updates
 * to avoid per-frame React re-renders and forced reflows.
 */
import { useRef, useEffect, useCallback } from "react";
import { motion, useAnimationFrame, useInView } from "framer-motion";
import { cn } from "@/lib/utils";
import ScratchEffects, { type ScratchEffect } from "./ScratchEffects";

interface TurntableProps {
  isSpinning: boolean;
  isBusy?: boolean;
  isActive?: boolean;
  onTogglePlay?: () => void;
  children?: React.ReactNode;
  vinylPosition?: 'sleeve' | 'traveling' | 'platter';
  scratchEffects?: ScratchEffect[];
}

const Turntable = ({ isSpinning, isBusy = false, isActive = false, onTogglePlay, children, vinylPosition, scratchEffects = [] }: TurntableProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(containerRef, { margin: "100px" });
  const strobeDots = Array.from({ length: 72 }, (_, i) => i);

  // Use refs instead of state for per-frame values to avoid re-renders
  const rotationRef = useRef(0);
  const currentSpeedRef = useRef(0);
  const targetSpeed = isSpinning ? 200 : 0;
  const lastTimeRef = useRef(performance.now());

  // DOM refs for direct style mutation
  const platterRef = useRef<HTMLDivElement>(null);
  const vinylWrapperRef = useRef<HTMLDivElement>(null);

  // Reset rotation when vinyl leaves the platter
  const prevPositionRef = useRef(vinylPosition);
  useEffect(() => {
    if (prevPositionRef.current === 'platter' && vinylPosition !== 'platter') {
      rotationRef.current = 0;
      currentSpeedRef.current = 0;
      // Update DOM immediately
      if (platterRef.current) platterRef.current.style.transform = `rotate(0deg)`;
      if (vinylWrapperRef.current) vinylWrapperRef.current.style.transform = '';
    }
    prevPositionRef.current = vinylPosition;
  }, [vinylPosition]);

  useAnimationFrame(() => {
    if (!isInView) return;
    const now = performance.now();
    const delta = (now - lastTimeRef.current) / 1000;
    lastTimeRef.current = now;

    const acceleration = isSpinning ? 150 : -80;
    let newSpeed = currentSpeedRef.current + acceleration * delta;

    if (isSpinning) {
      newSpeed = Math.min(newSpeed, targetSpeed);
    } else {
      newSpeed = Math.max(newSpeed, 0);
    }

    currentSpeedRef.current = newSpeed;
    if (newSpeed > 0) {
      rotationRef.current = (rotationRef.current + newSpeed * delta) % 360;
    }

    // Direct DOM updates — no React re-render
    const rot = `rotate(${rotationRef.current}deg)`;
    if (platterRef.current) {
      platterRef.current.style.transform = rot;
    }
    if (vinylWrapperRef.current && vinylPosition !== 'sleeve') {
      vinylWrapperRef.current.style.transform = rot;
    }
  });

  return (
    <div ref={containerRef} className="relative w-full h-full overflow-visible">
      {/* Turntable base - AT-LP120 style matte black */}
      <div className="absolute inset-0">
        {/* Outer cabinet/base - dark matte finish */}
        <div className="absolute inset-0 rounded bg-gradient-to-br from-[hsl(0,0%,12%)] to-[hsl(0,0%,6%)] shadow-2xl">
          <div className="absolute inset-0 rounded opacity-20 bg-[repeating-linear-gradient(90deg,transparent,transparent_1px,rgba(255,255,255,0.02)_1px,rgba(255,255,255,0.02)_2px)]" />
        </div>

        {/* Platter area */}
        <div
          className="absolute rounded-full"
          style={{
            top: '6%',
            left: '6%',
            width: '70%',
            aspectRatio: '1',
          }}
        >
          {/* Strobe ring recess */}
          <div className="absolute inset-0 rounded-full bg-[hsl(0,0%,5%)] shadow-inner">
            <div className="absolute inset-[2%] rounded-full border border-[hsl(0,0%,35%)]">
              {strobeDots.map((i) => (
                <div
                  key={i}
                  className="absolute w-[1.5%] h-[1.5%] rounded-full bg-[hsl(0,0%,65%)]"
                  style={{
                    top: '50%',
                    left: '50%',
                    transform: `rotate(${i * 5}deg) translateY(-48%) translateX(-50%)`,
                    transformOrigin: '0% 0%',
                  }}
                />
              ))}
            </div>
          </div>

          {/* Spinning platter with felt mat — uses ref for direct DOM rotation */}
          <div
            ref={platterRef}
            className="absolute inset-[6%] rounded-full bg-[hsl(0,0%,7%)]"
            style={{ willChange: 'transform' }}
          >
            {/* Felt mat texture */}
            <div className="absolute inset-0 rounded-full">
              {[...Array(6)].map((_, i) => (
                <div
                  key={i}
                  className="absolute rounded-full border border-[rgba(255,255,255,0.015)]"
                  style={{ inset: `${12 + i * 12}%` }}
                />
              ))}
            </div>

            {/* Center spindle */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[3%] h-[3%] min-w-2 min-h-2 rounded-full bg-[hsl(0,0%,4%)] shadow-inner">
              <div className="absolute inset-[20%] rounded-full bg-[hsl(0,0%,20%)]" />
            </div>
          </div>

          {/* Vinyl record — uses ref for direct DOM rotation */}
          <div
            ref={vinylWrapperRef}
            className="absolute inset-[6%] rounded-full overflow-visible pointer-events-none z-10"
          >
            <div className="absolute inset-0 overflow-visible pointer-events-auto">
              {children}
            </div>
          </div>

          {/* Non-rotating scratch effects overlay */}
          <div className="absolute inset-[6%] rounded-full pointer-events-none z-[15] overflow-hidden">
            <ScratchEffects effects={scratchEffects} />
          </div>
        </div>

        {/* Start/Stop button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (!isBusy) onTogglePlay?.();
          }}
          className={cn(
            "absolute bottom-[6%] left-[4%] group z-[5]",
            isBusy ? "cursor-not-allowed" : "cursor-pointer"
          )}
          disabled={isBusy}
          aria-label={isActive ? "Stop playback" : "Start playback"}
        >
          <div
            className={cn(
              "w-14 h-9 rounded border relative overflow-hidden flex items-center justify-center",
              isActive
                ? "bg-gradient-to-b from-[hsl(0,0%,6%)] to-[hsl(0,0%,3%)] border-[hsl(0,0%,10%)] shadow-[inset_0_2px_4px_rgba(0,0,0,0.6)]"
                : "bg-gradient-to-b from-[hsl(0,0%,22%)] to-[hsl(0,0%,12%)] border-[hsl(0,0%,28%)] shadow-[0_2px_6px_rgba(0,0,0,0.5),0_1px_2px_rgba(0,0,0,0.3)]",
              isBusy && "cursor-not-allowed"
            )}
          >
            <div className={cn(
              "absolute inset-[2px] rounded",
              isActive
                ? "bg-[hsl(0,0%,2%)]"
                : "bg-[hsl(0,0%,8%)] group-hover:bg-[hsl(0,0%,10%)]"
            )} />
            {!isActive && (
              <div className="absolute inset-0 bg-gradient-to-b from-white/8 to-transparent rounded" />
            )}
            {isActive && (
              <div className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-[hsl(120,80%,45%)] shadow-[0_0_4px_hsl(120,80%,45%)]" />
            )}
            <span className={cn(
              "relative z-10 text-[6px] font-body uppercase tracking-wider transition-colors",
              isActive
                ? "text-muted-foreground/50"
                : "text-muted-foreground/70 group-hover:text-muted-foreground"
            )}>
              Start/Stop
            </span>
          </div>
        </button>

        {/* Speed indicator LEDs */}
        <div className="absolute bottom-[18%] left-[5%] flex flex-col gap-1">
          <div className="flex items-center gap-1.5">
            <div className="w-1 h-1 rounded-full bg-[hsl(0,0%,25%)]" />
            <span className="text-[6px] text-muted-foreground/40">78</span>
          </div>
          <div className="flex items-center gap-1.5">
            <motion.div
              className={cn(
                "w-1 h-1 rounded-full",
                isSpinning ? "bg-destructive" : "bg-[hsl(0,0%,25%)]"
              )}
              animate={{
                boxShadow: isSpinning ? "0 0 4px hsl(var(--destructive))" : "none"
              }}
            />
            <span className="text-[6px] text-muted-foreground/40">33</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-1 h-1 rounded-full bg-[hsl(0,0%,25%)]" />
            <span className="text-[6px] text-muted-foreground/40">45</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Turntable;
